import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { X, Loader2, Mic, Check, Trash2, Pencil, ShieldAlert, Lightbulb, Plus, ArrowRight } from "lucide-react";
import { talkCapture, confirmCapture, captureImage, captureVoice } from "../lib/api";
import VoiceRecorder from "./VoiceRecorder";
import { TypeBadge, DomainBadge, UrgencyBadge } from "./Badges";
import { fmtDateTime } from "../lib/meta";

const SECTIONS = [
  { key: "ready", title: "Ready" },
  { key: "needs_check", title: "Needs a quick check" },
  { key: "not_sure", title: "Not sure" },
];

function sectionHint(key, n) {
  const many = n > 1;
  if (key === "ready") return many ? "These look good. Confirm, tweak, or let them go." : "This looks good. Confirm, tweak, or let it go.";
  if (key === "needs_check") return many ? "A few things need checking." : "One thing needs checking.";
  return many ? "I'm not sure about these." : "I'm not sure about this one.";
}

const STATE_DOT = {
  ready: "bg-[var(--pine)]",
  needs_check: "bg-[var(--amber)]",
  not_sure: "bg-[var(--ink4)]",
};

const PROCESSING_STAGES = [
  "Reading what you said\u2026",
  "Looking for what matters\u2026",
  "Checking timing\u2026",
  "Getting it ready\u2026",
];

// Voice goes straight from audio to extraction server-side, so the copy maps to the two
// real phases: transcription, then understanding.
const VOICE_STAGES = [
  "Reading what you said\u2026",
  "Understanding what matters\u2026",
];

const haptic = (ms = 10) => { try { navigator.vibrate && navigator.vibrate(ms); } catch (_) {} };

function timingLabel(it) {
  if (it.timing_state === "resolved" && it.suggested_datetime) return fmtDateTime(it.suggested_datetime);
  if (it.timing_state === "window" || it.timing_status === "window")
    return it.timing_phrase ? `Held for ${it.timing_phrase} \u00b7 no exact date` : "Held as a window";
  if (it.timing_state === "horizon") return it.timing_phrase ? `${it.timing_phrase} \u00b7 no exact time` : "Held for this week";
  if (it.timing_state === "needs_clarification") return "Timing needs checking";
  if (it.urgency === "TODAY" || it.urgency === "NOW") return "Today";
  if (it.urgency === "THIS_WEEK") return "This week";
  return "No specific time";
}

function ItemCard({ it, onEditTitle, onDiscard, onApplyCandidate, onSkip, onKeep }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(it.interpreted_summary);
  const save = () => { onEditTitle(title.trim() || it.interpreted_summary); setEditing(false); };
  const clar = it.clarification && !it._skipped ? it.clarification : null;

  if (it.is_sensitive) {
    return (
      <div className="rounded-2xl border border-[var(--amber-line,#e3d4ad)] bg-[var(--amber-soft,#fbf3df)] p-4" data-testid={`talk-item-${it._key}`}>
        <div className="flex items-start gap-2.5">
          <ShieldAlert size={17} strokeWidth={1.9} className="mt-0.5 shrink-0 text-[var(--amber-text)]" />
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold text-[var(--ink)]">This looks like {it.sensitive_label}.</p>
            <p className="text-xs text-[var(--ink3)] mt-1 leading-relaxed">
              I won't hold sensitive details like this as a normal item. You can let it go.
            </p>
          </div>
          <button onClick={onDiscard} data-testid={`talk-discard-${it._key}`} className="p-1.5 rounded-lg hover:bg-[var(--surface2)] text-[var(--ink3)] shrink-0" title="Discard">
            <Trash2 size={15} strokeWidth={1.9} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4" data-testid={`talk-item-${it._key}`}>
      <div className="flex items-start gap-2.5">
        <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${STATE_DOT[it.confidence_state]}`} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          {editing ? (
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={save}
              onKeyDown={(e) => e.key === "Enter" && save()}
              data-testid={`talk-edit-input-${it._key}`}
              className="w-full text-[15px] font-medium text-[var(--ink)] bg-[var(--surface2)] rounded-lg px-2 py-1 outline-none border border-[var(--pine-line)]"
            />
          ) : (
            <p className="text-[15px] font-medium text-[var(--ink)] leading-snug" data-testid={`talk-title-${it._key}`}>{title}</p>
          )}
          {it.confidence_state === "ready" ? (
            <details className="mt-1 group/quote">
              <summary className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink5)] cursor-pointer hover:text-[var(--ink3)] list-none select-none" data-testid={`talk-quote-toggle-${it._key}`}>
                From what you said
              </summary>
              <p className="text-xs text-[var(--ink4)] italic mt-1 leading-relaxed">“{it.raw_text}”</p>
            </details>
          ) : (
            <p className="text-xs text-[var(--ink4)] italic mt-1 leading-relaxed">“{it.raw_text}”</p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
            <TypeBadge type={it.type} />
            <DomainBadge domain={it.domain} />
            <UrgencyBadge urgency={it.urgency} />
            <span className="text-xs text-[var(--ink3)] bg-[var(--surface2)] rounded-full px-2 py-0.5">{timingLabel(it)}</span>
          </div>

          {clar && (
            <div className="mt-3 rounded-xl border border-[var(--line)] bg-[var(--surface2)] p-3" data-testid={`talk-clarify-${it._key}`}>
              <p className="text-[13px] font-semibold text-[var(--ink2)]">{clar.uncertainty}</p>
              <p className="text-[13px] text-[var(--ink3)] mt-0.5 leading-relaxed">{clar.question}</p>
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {clar.candidates.map((c, ci) => (
                  <button
                    key={ci}
                    onClick={() => onApplyCandidate(c)}
                    data-testid={`talk-candidate-${it._key}-${ci}`}
                    className="rounded-full border border-[var(--pine-line)] bg-[var(--surface)] hover:bg-[var(--pine-soft)] text-[var(--pine)] text-[13px] font-medium px-3 py-1.5 transition-colors"
                  >
                    {c.label}
                  </button>
                ))}
                <button
                  onClick={onSkip}
                  data-testid={`talk-skip-${it._key}`}
                  className="rounded-full text-[var(--ink4)] hover:text-[var(--ink2)] text-[13px] px-2 py-1.5 transition-colors"
                >
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {!clar && it.uncertainty_notes && it.uncertainty_notes.length > 0 && (
            <p className="text-xs text-[var(--ink3)] mt-2">{it.uncertainty_notes.join(" \u00b7 ")}</p>
          )}

          {it.confidence_state === "not_sure" && !it.is_sensitive && !clar && (
            it._kept ? (
              <p className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-medium text-[var(--pine)]" data-testid={`talk-kept-${it._key}`}>
                <Check size={13} strokeWidth={2.2} /> Will be saved
              </p>
            ) : (
              <div className="mt-2.5">
                <p className="text-xs text-[var(--ink4)] mb-1.5">Not saved unless you keep it or edit it.</p>
                <button
                  onClick={onKeep}
                  data-testid={`talk-keep-${it._key}`}
                  className="rounded-full border border-[var(--pine-line)] bg-[var(--surface)] hover:bg-[var(--pine-soft)] text-[var(--pine)] text-[13px] font-medium px-3 py-1.5 transition-colors"
                >
                  Keep this anyway
                </button>
              </div>
            )
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => setEditing((v) => !v)} data-testid={`talk-edit-${it._key}`} className="p-1.5 rounded-lg hover:bg-[var(--surface2)] text-[var(--ink3)]" title="Edit">
            <Pencil size={15} strokeWidth={1.9} />
          </button>
          <button onClick={onDiscard} data-testid={`talk-discard-${it._key}`} className="p-1.5 rounded-lg hover:bg-[var(--surface2)] text-[var(--ink3)]" title="Discard">
            <Trash2 size={15} strokeWidth={1.9} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TalkItThrough({ open, onClose, onSaved, initialText = "", autoProcess = false, initialImageFile = null, initialAudioFile = null, initialSource = "typed", onFallback, onGo }) {
  const [text, setText] = useState("");
  const [stage, setStage] = useState("input"); // input | processing | review | saved
  const [busy, setBusy] = useState(false);
  const [imageMode, setImageMode] = useState(false);
  const [audioMode, setAudioMode] = useState(false);
  const [recOpen, setRecOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [implied, setImplied] = useState([]);
  const [count, setCount] = useState(0);
  const [original, setOriginal] = useState("");
  const [procStep, setProcStep] = useState(0);
  const [savedInfo, setSavedInfo] = useState({ n: 0, noTime: 0 });
  const sourceRef = useRef("typed");
  const taRef = useRef(null);
  const autoRef = useRef(false);

  // Calm, sequential processing copy while the engine works.
  useEffect(() => {
    if (stage !== "processing") return;
    setProcStep(0);
    const id = setInterval(() => setProcStep((s) => s + 1), audioMode ? 1100 : 750);
    return () => clearInterval(id);
  }, [stage, audioMode]);

  // Unified surface: when opened pre-filled from the main Capture box, skip the modal's
  // own input and process immediately so it feels like one continuous moment.
  useEffect(() => {
    if (open && autoProcess && initialText.trim() && !autoRef.current) {
      autoRef.current = true;
      sourceRef.current = initialSource || "typed";
      runProcess(initialText.trim());
    }
    if (open && initialImageFile && !autoRef.current) {
      autoRef.current = true;
      runImageProcess(initialImageFile);
    }
    if (open && initialAudioFile && !autoRef.current) {
      autoRef.current = true;
      runAudioProcess(initialAudioFile);
    }
    if (!open) autoRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoProcess, initialText, initialImageFile, initialAudioFile]);

  if (!open) return null;

  const reset = () => {
    setText(""); setStage("input"); setItems([]); setImplied([]); setCount(0);
    setOriginal(""); sourceRef.current = "typed"; setSavedInfo({ n: 0, noTime: 0 });
    setImageMode(false); setAudioMode(false); setRecOpen(false);
  };
  const close = () => { reset(); onClose(); };

  // Voice capture: audio is transcribed AND extracted in a single server call (whisper-1 ->
  // same extract_items pipeline). We only render its review payload here — never partial text.
  const runAudioProcess = async (file) => {
    if (!file || busy) return;
    setBusy(true);
    setAudioMode(true);
    setImageMode(false);
    sourceRef.current = "voice";
    setStage("processing");
    try {
      const res = await captureVoice(file);
      if (res.unreadable || !(res.items && res.items.length)) {
        toast.error("I couldn\u2019t quite catch that. Try again, or type it instead.");
        close();
        return;
      }
      const withKeys = res.items.map((it, i) => ({ ...it, _key: `k${i}` }));
      setItems(withKeys);
      setImplied((res.implied_suggestions || []).map((s, i) => ({ ...s, _key: `s${i}` })));
      setCount(res.count || withKeys.length);
      setOriginal(res.transcript || "");
      setStage("review");
      haptic(12);
    } catch (e) {
      toast.error("I couldn\u2019t process that recording just now. Please try again, or type it instead.");
      close();
    } finally {
      setBusy(false);
    }
  };

  // Image capture: vision transcribes the screenshot on the backend, then the transcript runs
  // through the SAME extraction pipeline. We only render its review payload here.
  const runImageProcess = async (file) => {
    if (!file || busy) return;
    setBusy(true);
    setImageMode(true);
    setAudioMode(false);
    sourceRef.current = "image";
    setStage("processing");
    try {
      const res = await captureImage(file);
      if (res.unreadable || !(res.items && res.items.length)) {
        toast.error("Couldn\u2019t read that image. Try a clearer screenshot or type it instead.");
        close();
        return;
      }
      const withKeys = res.items.map((it, i) => ({ ...it, _key: `k${i}` }));
      setItems(withKeys);
      setImplied((res.implied_suggestions || []).map((s, i) => ({ ...s, _key: `s${i}` })));
      setCount(res.count || withKeys.length);
      setOriginal(res.transcript || "");
      setStage("review");
      haptic(12);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Couldn\u2019t read that image. Try a clearer screenshot or type it instead.");
      close();
    } finally {
      setBusy(false);
    }
  };

  const runProcess = async (value) => {
    const v = (value || "").trim();
    if (!v || busy) return;
    setBusy(true);
    setAudioMode(false);
    setOriginal(v);
    setText(v);
    setStage("processing");
    try {
      const res = await talkCapture(v, sourceRef.current);
      const withKeys = (res.items || []).map((it, i) => ({ ...it, _key: `k${i}` }));
      setItems(withKeys);
      setImplied((res.implied_suggestions || []).map((s, i) => ({ ...s, _key: `s${i}` })));
      setCount(res.count || withKeys.length);
      setStage("review");
      haptic(12);
    } catch (e) {
      // V1 internal fallback: never lose the user's words — hand them to the proven path.
      if (onFallback) { onFallback(v); reset(); }
      else { toast.error("Couldn\u2019t process that just now. Please try again."); setStage("input"); }
    } finally {
      setBusy(false);
    }
  };
  const process = () => runProcess(text);

  const patchItem = (key, patch) =>
    setItems((arr) => arr.map((it) => (it._key === key ? { ...it, ...patch } : it)));
  // Editing a "not sure" title is an explicit user decision — it becomes saveable.
  const editTitle = (key, newTitle) =>
    setItems((arr) => arr.map((it) => (
      it._key === key
        ? { ...it, interpreted_summary: newTitle, _kept: it.confidence_state === "not_sure" ? true : it._kept }
        : it
    )));
  const keepItem = (key) => patchItem(key, { _kept: true });
  const discard = (key) => setItems((arr) => arr.filter((it) => it._key !== key));
  const skipClarify = (key) => patchItem(key, { _skipped: true });
  const applyCandidate = (key, candidate) =>
    setItems((arr) => arr.map((it) => (
      it._key === key
        ? { ...it, ...(candidate.apply || {}), confidence_state: "ready", clarification: null, uncertainty_notes: [] }
        : it
    )));

  const addImplied = (s) => {
    setImplied((arr) => arr.filter((x) => x._key !== s._key));
    setItems((arr) => [...arr, {
      _key: `imp${Date.now()}`,
      raw_text: s.implied_from || s.suggested_title,
      interpreted_summary: s.suggested_title,
      type: "TASK", urgency: "LATER", domain: s.domain || "PERSONAL",
      suggested_datetime: null, explanation: s.reason || "",
      needs_review: false, timing_status: "none", timing_phrase: null,
      timing_state: "none", timing_signal_present: false, timing_signal_text: null,
      timing_confidence: "high", timing_reason: null,
      confidence_state: "ready", uncertainty_notes: [], clarification: null,
      is_sensitive: false, owner: "me", visibility: "private",
    }]);
  };
  const dismissImplied = (key) => setImplied((arr) => arr.filter((x) => x._key !== key));

  // A "not sure" item is only committed if the user explicitly kept or edited it. Everything
  // else non-sensitive is savable. This is the single source of truth for save + count.
  const isSavable = (it) => !it.is_sensitive && (it.confidence_state !== "not_sure" || it._kept);

  const save = async () => {
    const keep = items.filter(isSavable);
    if (busy || keep.length === 0) return;
    setBusy(true);
    try {
      const drafts = keep.map(({ _key, _skipped, _kept, confidence_state, field_confidence, uncertainty_notes,
        clarification, is_sensitive, sensitive_label, source_marker, stream_position,
        is_date_anchor, _group_ref, _anchor_ref, ...d }) => ({
          ...d,
          needs_review: confidence_state !== "ready" ? true : d.needs_review,
          is_anchor: !!is_date_anchor,
          group_ref: _group_ref || null,
          anchor_ref: _anchor_ref || null,
        }));
      const res = await confirmCapture(drafts);
      const n = Array.isArray(res) ? res.length : (res?.saved ?? drafts.length);
      const noTime = keep.filter((it) => !it.suggested_datetime
        && !["TODAY", "NOW", "THIS_WEEK"].includes(it.urgency)).length;
      haptic(16);
      if (onSaved) onSaved();
      // A 3+ batch did something significant — land the user calmly before they move on.
      if (n >= 3) {
        setSavedInfo({ n, noTime });
        setStage("saved");
      } else {
        toast.success(`Held. ${n} ${n === 1 ? "thing is" : "things are"} safe with me.`);
        close();
      }
    } catch (e) {
      toast.error("Couldn\u2019t save those just now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const savable = items.filter(isSavable).length;
  const readyCount = items.filter((it) => !it.is_sensitive && it.confidence_state === "ready").length;
  const saveLabel = savable === 0
    ? "Save"
    : savable === 1
      ? "Save 1 thing"
      : savable === readyCount
        ? `Save ${savable} ready things`
        : `Save ${savable} things`;
  const src = imageMode ? "your image" : "what you said";
  const headingCount = count
    ? `${count} ${count === 1 ? "thing" : "things"} found in ${src}.`
    : `Nothing found in ${src}.`;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="talk-overlay">
      <div className="w-full sm:max-w-2xl bg-[var(--bg)] sm:rounded-3xl rounded-t-3xl max-h-[92vh] flex flex-col shadow-[0_-8px_40px_rgba(0,0,0,0.18)]">
        <div className="flex items-center justify-between px-5 sm:px-7 py-4 border-b border-[var(--line)] shrink-0">
          <span className="font-serif-display text-[17px] text-[var(--ink)]">Review</span>
          <button onClick={close} data-testid="talk-close" className="p-1.5 rounded-lg hover:bg-[var(--surface2)] text-[var(--ink3)]"><X size={18} /></button>
        </div>

        {stage === "input" && (
          <div className="px-5 sm:px-7 py-6 overflow-y-auto">
            <p className="text-[var(--ink2)] text-[15px] leading-relaxed mb-4">
              Put it all down in one go — say it, type it, or paste it. Take as long as you need. When you're done, I'll pull out what matters and let you confirm.
            </p>
            <textarea
              ref={taRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={() => { if (sourceRef.current === "typed") sourceRef.current = "pasted"; }}
              data-testid="talk-input"
              placeholder="e.g. I need to call grandma this weekend, sort the school form for Friday, and remind me after the meeting to send the proposal…"
              className="w-full bg-[var(--surface)] border border-[var(--line)] rounded-2xl p-4 text-[15px] leading-relaxed text-[var(--ink)] outline-none focus:border-[var(--pine-line)] resize-none min-h-[180px]"
            />
            <div className="flex items-center justify-between mt-4 gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <button
                  onClick={() => setRecOpen(true)}
                  data-testid="talk-mic-btn"
                  title="Record"
                  className="relative h-11 w-11 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 bg-[var(--pine)] text-[var(--on-pine)] hover:bg-[var(--pine-dark)]"
                >
                  <Mic size={18} strokeWidth={1.75} />
                </button>
                <span className="text-xs text-[var(--ink3)] truncate" data-testid="talk-mic-status">
                  Record your voice, or type / paste above.
                </span>
              </div>
              <button
                onClick={process}
                disabled={!text.trim() || busy}
                data-testid="talk-process-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] disabled:opacity-40 text-[var(--on-pine)] font-semibold px-6 py-3 text-sm transition-colors shrink-0"
              >
                <ArrowRight size={16} strokeWidth={2} />
                I'm done
              </button>
            </div>
          </div>
        )}

        {stage === "processing" && (
          <div className="px-5 sm:px-7 py-16 flex flex-col items-center justify-center text-center" data-testid="talk-processing">
            <span className="h-11 w-11 rounded-full bg-[var(--surface2)] flex items-center justify-center">
              <span className="h-3 w-3 rounded-full bg-[var(--pine)] breathe" />
            </span>
            <p className="mt-5 text-[var(--ink2)] text-[15px] font-medium" data-testid="talk-processing-copy">
              {imageMode
                ? "Reading your image\u2026"
                : audioMode
                  ? VOICE_STAGES[Math.min(procStep, VOICE_STAGES.length - 1)]
                  : PROCESSING_STAGES[Math.min(procStep, PROCESSING_STAGES.length - 1)]}
            </p>
          </div>
        )}

        {stage === "review" && (
          <>
            <div className="px-5 sm:px-7 pt-5 pb-3 shrink-0 border-b border-[var(--line)]">
              <h2 className="font-serif-display text-xl text-[var(--ink)]" data-testid="talk-review-heading">{headingCount}</h2>
              {original && (
                <details className="mt-3 group" data-testid="talk-original">
                  <summary className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--ink4)] cursor-pointer hover:text-[var(--ink3)] list-none">
                    {imageMode ? "What I read" : "What you said"}
                  </summary>
                  <p className="mt-2 text-[13px] text-[var(--ink3)] italic leading-relaxed max-h-28 overflow-y-auto bg-[var(--surface2)] rounded-xl px-3.5 py-2.5">
                    “{original}”
                  </p>
                </details>
              )}
            </div>
            <div className="px-5 sm:px-7 py-4 overflow-y-auto flex-1 space-y-6">
              {items.length === 0 && implied.length === 0 && (
                <p className="text-[var(--ink3)] text-sm py-8 text-center" data-testid="talk-empty">Nothing left to save. Close this, or start again.</p>
              )}
              {SECTIONS.map((sec) => {
                const secItems = items.filter((it) => it.confidence_state === sec.key);
                if (secItems.length === 0) return null;
                return (
                  <div key={sec.key} data-testid={`talk-section-${sec.key}`}>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className={`h-2 w-2 rounded-full ${STATE_DOT[sec.key]}`} />
                      <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-[var(--ink3)]">{sec.title}</h3>
                      <span className="text-xs text-[var(--ink4)]">{secItems.length}</span>
                    </div>
                    <p className="text-xs text-[var(--ink4)] mb-3">{sectionHint(sec.key, secItems.length)}</p>
                    <div className="space-y-3">
                      {secItems.map((it) => (
                        <ItemCard
                          key={it._key}
                          it={it}
                          onEditTitle={(t) => editTitle(it._key, t)}
                          onDiscard={() => discard(it._key)}
                          onApplyCandidate={(c) => applyCandidate(it._key, c)}
                          onSkip={() => skipClarify(it._key)}
                          onKeep={() => keepItem(it._key)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}

              {implied.length > 0 && (
                <div data-testid="talk-section-implied">
                  <div className="flex items-baseline gap-2 mb-2">
                    <Lightbulb size={14} strokeWidth={1.9} className="text-[var(--ink4)]" />
                    <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-[var(--ink3)]">I noticed</h3>
                  </div>
                  <p className="text-xs text-[var(--ink4)] mb-3">These might be worth adding.</p>
                  <div className="space-y-3">
                    {implied.map((s) => (
                      <div key={s._key} className="rounded-2xl border border-dashed border-[var(--line)] bg-[var(--surface)] p-4 flex items-start justify-between gap-3" data-testid={`talk-implied-${s._key}`}>
                        <div className="min-w-0">
                          <p className="text-[15px] font-medium text-[var(--ink)] leading-snug">{s.suggested_title}</p>
                          {s.reason && <p className="text-xs text-[var(--ink3)] mt-1">{s.reason}</p>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => addImplied(s)} data-testid={`talk-implied-add-${s._key}`} className="inline-flex items-center gap-1 rounded-full border border-[var(--pine-line)] text-[var(--pine)] text-xs font-medium px-2.5 py-1 hover:bg-[var(--pine-soft)]">
                            <Plus size={13} /> Add
                          </button>
                          <button onClick={() => dismissImplied(s._key)} data-testid={`talk-implied-dismiss-${s._key}`} className="p-1.5 rounded-lg hover:bg-[var(--surface2)] text-[var(--ink4)]" title="Dismiss"><X size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 sm:px-7 py-4 sheet-safe-bottom border-t border-[var(--line)] shrink-0 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <button onClick={() => setStage("input")} data-testid="talk-keep-editing" className="text-sm text-[var(--ink3)] hover:text-[var(--ink)]">Keep editing</button>
                <button onClick={reset} data-testid="talk-start-over" className="text-sm text-[var(--ink4)] hover:text-[var(--ink2)]">Start over</button>
              </div>
              <button
                onClick={save}
                disabled={busy || savable === 0}
                data-testid="talk-save-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] disabled:opacity-40 text-[var(--on-pine)] font-semibold px-6 py-3 text-sm transition-colors"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} strokeWidth={2} />}
                {saveLabel}
              </button>
            </div>
          </>
        )}

        {stage === "saved" && (
          <div className="px-5 sm:px-7 py-12 sm:py-16 flex flex-col items-center text-center" data-testid="talk-saved-landing">
            <span className="h-12 w-12 rounded-full bg-[var(--pine-soft)] flex items-center justify-center">
              <Check size={22} strokeWidth={2} className="text-[var(--pine)]" />
            </span>
            <h2 className="font-serif-display text-2xl text-[var(--ink)] mt-5" data-testid="talk-saved-count">
              Saved {savedInfo.n} {savedInfo.n === 1 ? "thing" : "things"}.
            </h2>
            <p className="text-[var(--ink3)] text-[15px] mt-2 leading-relaxed max-w-sm">
              {savedInfo.noTime > 0
                ? "A few need a time when you're ready. No rush — they're held safely."
                : "They're held safely. I'll bring each one back when it matters."}
            </p>
            <div className="flex flex-col w-full max-w-xs gap-2.5 mt-8">
              <button
                onClick={() => (onGo ? onGo("/inbox") : close())}
                data-testid="talk-saved-see-inbox"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] font-semibold px-6 py-3 text-sm transition-colors"
              >
                See what was saved
              </button>
              <button
                onClick={() => (onGo ? onGo("/ahead") : close())}
                data-testid="talk-saved-see-ahead"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[var(--line)] hover:bg-[var(--surface2)] text-[var(--ink2)] font-medium px-6 py-3 text-sm transition-colors"
              >
                See what's coming up
              </button>
              <button
                onClick={close}
                data-testid="talk-saved-keep-going"
                className="text-sm text-[var(--ink4)] hover:text-[var(--ink2)] mt-1 transition-colors"
              >
                Keep going
              </button>
            </div>
          </div>
        )}
      </div>

      <VoiceRecorder
        open={recOpen}
        onCancel={() => setRecOpen(false)}
        onComplete={(file) => { setRecOpen(false); runAudioProcess(file); }}
      />
    </div>
  );
}
